import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  listBankAccounts, listBankStatements, createBankStatement, fetchBankStatement,
  importBankStatementCSV, listBankStatementLines, createBankStatementLine,
  matchBankStatementLine, unmatchBankStatementLine,
  listBankReconciliations, createBankReconciliation, completeBankReconciliation,
  listJournalEntries,
} from '../api';

function BankReconciliation({ authAxios, showFormMessage, userRole }) {
  const [activeTab, setActiveTab] = useState('statements');
  const [bankAccounts, setBankAccounts] = useState([]);
  const [selectedBankAccount, setSelectedBankAccount] = useState('');
  const [statements, setStatements] = useState([]);
  const [selectedStatement, setSelectedStatement] = useState(null);
  const [statementLines, setStatementLines] = useState([]);
  const [reconciliations, setReconciliations] = useState([]);
  const [journalEntries, setJournalEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Statement form
  const [showStatementForm, setShowStatementForm] = useState(false);
  const [statementForm, setStatementForm] = useState({
    bank_account: '', period_start: '', period_end: '', opening_balance: '0.00', closing_balance: '0.00', notes: '',
  });

  // Line form
  const [showLineForm, setShowLineForm] = useState(false);
  const [lineForm, setLineForm] = useState({
    date: '', description: '', reference: '', debit: '0.00', credit: '0.00', balance: '',
  });

  // Recon form
  const [showReconForm, setShowReconForm] = useState(false);
  const [reconForm, setReconForm] = useState({
    bank_account: '', statement: '', period_start: '', period_end: '',
    statement_balance: '0.00', gl_balance: '0.00',
  });

  // Matching state
  const [matchingLineId, setMatchingLineId] = useState(null);

  const canManage = useMemo(() => ['PARTNER', 'SUPERUSER', 'ACCOUNTANT'].includes(userRole), [userRole]);

  const fetchBankAccounts = useCallback(async () => {
    try {
      const data = await listBankAccounts(authAxios);
      setBankAccounts(Array.isArray(data) ? data : []);
    } catch { /* ignore */ }
  }, [authAxios]);

  const fetchStatements = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = selectedBankAccount ? { bank_account: selectedBankAccount } : {};
      const data = await listBankStatements(authAxios, params);
      setStatements(Array.isArray(data) ? data : []);
    } catch {
      showFormMessage('Failed to load statements.', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [authAxios, selectedBankAccount, showFormMessage]);

  const fetchLines = useCallback(async (stmtId) => {
    try {
      const data = await listBankStatementLines(authAxios, { statement: stmtId });
      setStatementLines(Array.isArray(data) ? data : []);
    } catch { /* ignore */ }
  }, [authAxios]);

  const fetchRecons = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = selectedBankAccount ? { bank_account: selectedBankAccount } : {};
      const data = await listBankReconciliations(authAxios, params);
      setReconciliations(Array.isArray(data) ? data : []);
    } catch {
      showFormMessage('Failed to load reconciliations.', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [authAxios, selectedBankAccount, showFormMessage]);

  const fetchJournals = useCallback(async () => {
    try {
      const data = await listJournalEntries(authAxios);
      setJournalEntries(Array.isArray(data) ? data : []);
    } catch { /* ignore */ }
  }, [authAxios]);

  useEffect(() => { fetchBankAccounts(); }, [fetchBankAccounts]);
  useEffect(() => {
    if (activeTab === 'statements') fetchStatements();
    else if (activeTab === 'reconciliations') fetchRecons();
  }, [activeTab, fetchStatements, fetchRecons]);

  // Create statement
  const handleCreateStatement = async (e) => {
    e.preventDefault();
    try {
      await createBankStatement(authAxios, statementForm);
      showFormMessage('Statement created.', 'success');
      setShowStatementForm(false);
      fetchStatements();
    } catch (err) {
      showFormMessage(err.response?.data?.detail || 'Failed to create statement.', 'error');
    }
  };

  // Import CSV
  const handleImportCSV = async (stmtId) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      try {
        const result = await importBankStatementCSV(authAxios, stmtId, file);
        showFormMessage(result.detail || 'CSV imported.', 'success');
        if (selectedStatement?.id === stmtId) fetchLines(stmtId);
      } catch (err) {
        showFormMessage(err.response?.data?.detail || 'CSV import failed.', 'error');
      }
    };
    input.click();
  };

  // View statement lines
  const handleViewStatement = async (stmt) => {
    setSelectedStatement(stmt);
    fetchLines(stmt.id);
    fetchJournals();
  };

  // Add manual line
  const handleAddLine = async (e) => {
    e.preventDefault();
    try {
      await createBankStatementLine(authAxios, { ...lineForm, statement: selectedStatement.id });
      showFormMessage('Line added.', 'success');
      setShowLineForm(false);
      setLineForm({ date: '', description: '', reference: '', debit: '0.00', credit: '0.00', balance: '' });
      fetchLines(selectedStatement.id);
    } catch (err) {
      showFormMessage(err.response?.data?.detail || 'Failed to add line.', 'error');
    }
  };

  // Match / unmatch
  const handleMatch = async (lineId, jeId) => {
    try {
      await matchBankStatementLine(authAxios, lineId, jeId);
      showFormMessage('Line matched.', 'success');
      setMatchingLineId(null);
      fetchLines(selectedStatement.id);
    } catch (err) {
      showFormMessage(err.response?.data?.detail || 'Match failed.', 'error');
    }
  };

  const handleUnmatch = async (lineId) => {
    try {
      await unmatchBankStatementLine(authAxios, lineId);
      showFormMessage('Match removed.', 'success');
      fetchLines(selectedStatement.id);
    } catch {
      showFormMessage('Unmatch failed.', 'error');
    }
  };

  // Create reconciliation
  const handleCreateRecon = async (e) => {
    e.preventDefault();
    try {
      await createBankReconciliation(authAxios, reconForm);
      showFormMessage('Reconciliation created.', 'success');
      setShowReconForm(false);
      fetchRecons();
    } catch (err) {
      showFormMessage(err.response?.data?.detail || 'Failed to create reconciliation.', 'error');
    }
  };

  const handleCompleteRecon = async (id) => {
    if (!window.confirm('Complete this reconciliation? This action cannot be undone.')) return;
    try {
      await completeBankReconciliation(authAxios, id);
      showFormMessage('Reconciliation completed.', 'success');
      fetchRecons();
    } catch (err) {
      showFormMessage(err.response?.data?.detail || 'Failed to complete.', 'error');
    }
  };

  const tabs = [
    { key: 'statements', label: 'Bank Statements' },
    { key: 'reconciliations', label: 'Reconciliations' },
  ];

  return (
    <div className="p-4">
      <h2 className="text-xl font-bold text-gray-800 mb-3">Bank Reconciliation</h2>

      {/* Bank account selector */}
      <div className="mb-4 flex items-center gap-3">
        <label className="text-sm font-medium text-gray-600">Bank Account:</label>
        <select value={selectedBankAccount} onChange={e => setSelectedBankAccount(e.target.value)}
          className="border rounded px-3 py-1.5 text-sm" style={{ minWidth: 220 }}>
          <option value="">All Accounts</option>
          {bankAccounts.map(ba => (
            <option key={ba.id} value={ba.id}>{ba.account_name} – {ba.bank_name}</option>
          ))}
        </select>
      </div>

      {/* Tabs */}
      <div className="flex border-b mb-4">
        {tabs.map(t => (
          <button key={t.key} onClick={() => { setActiveTab(t.key); setSelectedStatement(null); }}
            className={`px-4 py-2 text-sm font-medium ${activeTab === t.key ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ============ STATEMENTS TAB ============ */}
      {activeTab === 'statements' && !selectedStatement && (
        <div>
          {canManage && (
            <div className="mb-3">
              <button onClick={() => { setStatementForm(f => ({ ...f, bank_account: selectedBankAccount })); setShowStatementForm(true); }}
                className="bg-blue-600 text-white px-3 py-1.5 rounded text-sm hover:bg-blue-700">+ New Statement</button>
            </div>
          )}
          {showStatementForm && (
            <div className="bg-white border rounded shadow p-4 mb-4">
              <h3 className="font-semibold mb-2 text-sm">New Bank Statement</h3>
              <form onSubmit={handleCreateStatement} className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Bank Account *</label>
                  <select name="bank_account" value={statementForm.bank_account}
                    onChange={e => setStatementForm(f => ({ ...f, bank_account: e.target.value }))} required
                    className="w-full border rounded px-3 py-2 text-sm">
                    <option value="">Select…</option>
                    {bankAccounts.map(ba => <option key={ba.id} value={ba.id}>{ba.account_name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Period Start *</label>
                  <input type="date" value={statementForm.period_start}
                    onChange={e => setStatementForm(f => ({ ...f, period_start: e.target.value }))} required
                    className="w-full border rounded px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Period End *</label>
                  <input type="date" value={statementForm.period_end}
                    onChange={e => setStatementForm(f => ({ ...f, period_end: e.target.value }))} required
                    className="w-full border rounded px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Opening Balance</label>
                  <input type="number" step="0.01" value={statementForm.opening_balance}
                    onChange={e => setStatementForm(f => ({ ...f, opening_balance: e.target.value }))}
                    className="w-full border rounded px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Closing Balance</label>
                  <input type="number" step="0.01" value={statementForm.closing_balance}
                    onChange={e => setStatementForm(f => ({ ...f, closing_balance: e.target.value }))}
                    className="w-full border rounded px-3 py-2 text-sm" />
                </div>
                <div className="flex items-end gap-2">
                  <button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700">Create</button>
                  <button type="button" onClick={() => setShowStatementForm(false)} className="bg-gray-200 px-4 py-2 rounded text-sm">Cancel</button>
                </div>
              </form>
            </div>
          )}

          {isLoading ? <p className="text-gray-500 text-sm">Loading…</p> : (
            <table className="min-w-full bg-white border rounded text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left">Account</th>
                  <th className="px-3 py-2 text-left">Period</th>
                  <th className="px-3 py-2 text-right">Opening</th>
                  <th className="px-3 py-2 text-right">Closing</th>
                  <th className="px-3 py-2 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {statements.map(s => (
                  <tr key={s.id} className="border-t hover:bg-gray-50">
                    <td className="px-3 py-2">{s.bank_account_name}</td>
                    <td className="px-3 py-2">{s.period_start} – {s.period_end}</td>
                    <td className="px-3 py-2 text-right">{Number(s.opening_balance).toLocaleString('en', {minimumFractionDigits: 2})}</td>
                    <td className="px-3 py-2 text-right">{Number(s.closing_balance).toLocaleString('en', {minimumFractionDigits: 2})}</td>
                    <td className="px-3 py-2 text-center">
                      <button onClick={() => handleViewStatement(s)} className="text-blue-600 text-xs hover:underline mr-2">View Lines</button>
                      {canManage && <button onClick={() => handleImportCSV(s.id)} className="text-green-600 text-xs hover:underline">Import CSV</button>}
                    </td>
                  </tr>
                ))}
                {statements.length === 0 && (
                  <tr><td colSpan={5} className="px-3 py-4 text-center text-gray-400 italic">No statements found.</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ============ STATEMENT LINES DETAIL ============ */}
      {activeTab === 'statements' && selectedStatement && (
        <div>
          <button onClick={() => setSelectedStatement(null)} className="text-blue-600 text-sm hover:underline mb-3">← Back to Statements</button>
          <h3 className="font-semibold mb-2">
            {selectedStatement.bank_account_name}: {selectedStatement.period_start} – {selectedStatement.period_end}
          </h3>

          {canManage && (
            <div className="mb-3 flex gap-2">
              <button onClick={() => setShowLineForm(true)} className="bg-blue-600 text-white px-3 py-1.5 rounded text-sm hover:bg-blue-700">+ Add Line</button>
              <button onClick={() => handleImportCSV(selectedStatement.id)} className="bg-green-600 text-white px-3 py-1.5 rounded text-sm hover:bg-green-700">Import CSV</button>
            </div>
          )}

          {showLineForm && (
            <div className="bg-white border rounded shadow p-4 mb-4">
              <form onSubmit={handleAddLine} className="grid grid-cols-2 md:grid-cols-6 gap-2">
                <input type="date" value={lineForm.date} onChange={e => setLineForm(f => ({ ...f, date: e.target.value }))}
                  required className="border rounded px-2 py-1.5 text-sm" placeholder="Date" />
                <input value={lineForm.description} onChange={e => setLineForm(f => ({ ...f, description: e.target.value }))}
                  required className="border rounded px-2 py-1.5 text-sm col-span-2" placeholder="Description" />
                <input type="number" step="0.01" value={lineForm.debit} onChange={e => setLineForm(f => ({ ...f, debit: e.target.value }))}
                  className="border rounded px-2 py-1.5 text-sm" placeholder="Debit" />
                <input type="number" step="0.01" value={lineForm.credit} onChange={e => setLineForm(f => ({ ...f, credit: e.target.value }))}
                  className="border rounded px-2 py-1.5 text-sm" placeholder="Credit" />
                <div className="flex gap-1">
                  <button type="submit" className="bg-blue-600 text-white px-3 py-1 rounded text-xs">Add</button>
                  <button type="button" onClick={() => setShowLineForm(false)} className="bg-gray-200 px-3 py-1 rounded text-xs">Cancel</button>
                </div>
              </form>
            </div>
          )}

          <table className="min-w-full bg-white border rounded text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left">Date</th>
                <th className="px-3 py-2 text-left">Description</th>
                <th className="px-3 py-2 text-left">Reference</th>
                <th className="px-3 py-2 text-right">Debit</th>
                <th className="px-3 py-2 text-right">Credit</th>
                <th className="px-3 py-2 text-right">Balance</th>
                <th className="px-3 py-2 text-center">Reconciled</th>
                <th className="px-3 py-2 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {statementLines.map(line => (
                <tr key={line.id} className={`border-t ${line.is_reconciled ? 'bg-green-50' : 'hover:bg-gray-50'}`}>
                  <td className="px-3 py-2">{line.date}</td>
                  <td className="px-3 py-2">{line.description}</td>
                  <td className="px-3 py-2">{line.reference}</td>
                  <td className="px-3 py-2 text-right">{Number(line.debit).toFixed(2)}</td>
                  <td className="px-3 py-2 text-right">{Number(line.credit).toFixed(2)}</td>
                  <td className="px-3 py-2 text-right">{line.balance != null ? Number(line.balance).toFixed(2) : '—'}</td>
                  <td className="px-3 py-2 text-center">
                    {line.is_reconciled ? (
                      <span className="text-green-600 font-medium" title={`Matched: ${line.matched_journal_entry_number || ''}`}>✓</span>
                    ) : '—'}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {line.is_reconciled ? (
                      <button onClick={() => handleUnmatch(line.id)} className="text-red-500 text-xs hover:underline">Unmatch</button>
                    ) : matchingLineId === line.id ? (
                      <div className="flex items-center gap-1">
                        <select className="border rounded text-xs px-1 py-0.5" style={{ maxWidth: 180 }}
                          onChange={e => { if (e.target.value) handleMatch(line.id, e.target.value); }}>
                          <option value="">Select JE…</option>
                          {journalEntries.map(je => (
                            <option key={je.id} value={je.id}>{je.entry_number} – {je.description?.substring(0, 30)}</option>
                          ))}
                        </select>
                        <button onClick={() => setMatchingLineId(null)} className="text-gray-400 text-xs">✗</button>
                      </div>
                    ) : (
                      <button onClick={() => setMatchingLineId(line.id)} className="text-blue-600 text-xs hover:underline">Match</button>
                    )}
                  </td>
                </tr>
              ))}
              {statementLines.length === 0 && (
                <tr><td colSpan={8} className="px-3 py-4 text-center text-gray-400 italic">No lines. Add manually or import CSV.</td></tr>
              )}
            </tbody>
          </table>

          <div className="mt-3 text-sm text-gray-500">
            Reconciled: {statementLines.filter(l => l.is_reconciled).length} / {statementLines.length} lines
          </div>
        </div>
      )}

      {/* ============ RECONCILIATIONS TAB ============ */}
      {activeTab === 'reconciliations' && (
        <div>
          {canManage && (
            <div className="mb-3">
              <button onClick={() => { setReconForm(f => ({ ...f, bank_account: selectedBankAccount })); setShowReconForm(true); }}
                className="bg-blue-600 text-white px-3 py-1.5 rounded text-sm hover:bg-blue-700">+ New Reconciliation</button>
            </div>
          )}

          {showReconForm && (
            <div className="bg-white border rounded shadow p-4 mb-4">
              <h3 className="font-semibold mb-2 text-sm">New Bank Reconciliation</h3>
              <form onSubmit={handleCreateRecon} className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Bank Account *</label>
                  <select value={reconForm.bank_account}
                    onChange={e => setReconForm(f => ({ ...f, bank_account: e.target.value }))} required
                    className="w-full border rounded px-3 py-2 text-sm">
                    <option value="">Select…</option>
                    {bankAccounts.map(ba => <option key={ba.id} value={ba.id}>{ba.account_name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Statement (optional)</label>
                  <select value={reconForm.statement}
                    onChange={e => setReconForm(f => ({ ...f, statement: e.target.value }))}
                    className="w-full border rounded px-3 py-2 text-sm">
                    <option value="">None</option>
                    {statements.map(s => <option key={s.id} value={s.id}>{s.period_start} – {s.period_end}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Period Start *</label>
                  <input type="date" value={reconForm.period_start}
                    onChange={e => setReconForm(f => ({ ...f, period_start: e.target.value }))} required
                    className="w-full border rounded px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Period End *</label>
                  <input type="date" value={reconForm.period_end}
                    onChange={e => setReconForm(f => ({ ...f, period_end: e.target.value }))} required
                    className="w-full border rounded px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Statement Balance *</label>
                  <input type="number" step="0.01" value={reconForm.statement_balance}
                    onChange={e => setReconForm(f => ({ ...f, statement_balance: e.target.value }))} required
                    className="w-full border rounded px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">GL Balance *</label>
                  <input type="number" step="0.01" value={reconForm.gl_balance}
                    onChange={e => setReconForm(f => ({ ...f, gl_balance: e.target.value }))} required
                    className="w-full border rounded px-3 py-2 text-sm" />
                </div>
                <div className="md:col-span-3 flex gap-2">
                  <button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700">Create</button>
                  <button type="button" onClick={() => setShowReconForm(false)} className="bg-gray-200 px-4 py-2 rounded text-sm">Cancel</button>
                </div>
              </form>
            </div>
          )}

          {isLoading ? <p className="text-gray-500 text-sm">Loading…</p> : (
            <table className="min-w-full bg-white border rounded text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left">Account</th>
                  <th className="px-3 py-2 text-left">Period</th>
                  <th className="px-3 py-2 text-right">Statement Bal</th>
                  <th className="px-3 py-2 text-right">GL Bal</th>
                  <th className="px-3 py-2 text-right">Difference</th>
                  <th className="px-3 py-2 text-center">Status</th>
                  {canManage && <th className="px-3 py-2 text-center">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {reconciliations.map(r => (
                  <tr key={r.id} className="border-t hover:bg-gray-50">
                    <td className="px-3 py-2">{r.bank_account_name}</td>
                    <td className="px-3 py-2">{r.period_start} – {r.period_end}</td>
                    <td className="px-3 py-2 text-right">{Number(r.statement_balance).toLocaleString('en', {minimumFractionDigits: 2})}</td>
                    <td className="px-3 py-2 text-right">{Number(r.gl_balance).toLocaleString('en', {minimumFractionDigits: 2})}</td>
                    <td className={`px-3 py-2 text-right font-medium ${Number(r.difference) === 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {Number(r.difference).toLocaleString('en', {minimumFractionDigits: 2})}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-xs ${r.status === 'COMPLETED' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                        {r.status_display || r.status}
                      </span>
                    </td>
                    {canManage && (
                      <td className="px-3 py-2 text-center">
                        {r.status === 'DRAFT' && (
                          <button onClick={() => handleCompleteRecon(r.id)} className="text-green-600 text-xs hover:underline">Complete</button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
                {reconciliations.length === 0 && (
                  <tr><td colSpan={7} className="px-3 py-4 text-center text-gray-400 italic">No reconciliations found.</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

export default BankReconciliation;
